import { infiniteQueryOptions, queryOptions, type InfiniteData } from "@tanstack/react-query";
import type { IWorkspaceMember } from "@/types/workspace";
import type {
  IWorkspaceMembersListQuery,
  IWorkspaceMembersPage,
  WorkspaceService,
} from "@/services/workspace.service";
import { getQueryClient, peekQueryClient } from "./client";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, scopeIfReady, type QueryScope } from "./scope";

/**
 * The workspace-member reads the caching pilot moves onto Query (NG-2546,
 * contract §2):
 *
 * - **R20** `members.bank`: every member of the workspace,
 *   `[slug, uid, 'members', 'bank']`. Reference data: the pickers, filters and
 *   name lookups that read it need names and ids, which rarely change.
 * - **R21** `members.page`: the settings Members table, server-filtered,
 *   sorted and walked by cursor, `[slug, uid, 'members', 'page', {…}]`. Mutable,
 *   because it is the screen where admins edit those rows.
 * - **R22** `member.eligibleRoles`: which roles one member may be given,
 *   `[slug, uid, 'member', mid, 'eligible-roles']`. Access-sensitive: it is the
 *   check a role picker runs before offering a role.
 *
 * R20 and R21 sit under `'members'` so one prefix invalidation reaches the bank
 * and every page variant; R22 sits under `'member', mid` beside the member's
 * other per-member reads. All three are plain idempotent GETs, so all declare
 * `"safe-read"` and `runRead` owns their retries.
 *
 * The definitions take the service rather than reaching for the root store, so
 * a store passes `rootStore.workspaceService` and a test passes a fake.
 */

/** A role assignment that keeps a member from being given another role. */
interface IRoleBlocking {
  role: string;
  resource_type: string;
  resource_id: string;
  resource_name: string;
}

interface IRoleEligibility {
  eligible: boolean;
  blocking_roles: IRoleBlocking[];
}

/** R22's answer: per-role-slug eligibility for one member. */
export type MemberRoleEligibility = Record<string, IRoleEligibility>;

/** The parameters of R21 that change the response body; the cursor is the page param. */
export type MembersPageQuery = Omit<IWorkspaceMembersListQuery, "cursor">;

/** The R20 key. */
export function memberBankKey(scope: QueryScope) {
  return scopedKey(scope, "members", "bank");
}

/**
 * Whether `queryKey` is an R20 key in any scope. A member id is unique across
 * workspaces, so a member write patches every cached bank without naming one.
 * Built from `memberBankKey` itself, so a change to the key reaches the match.
 */
export function isMemberBankKey(queryKey: readonly unknown[]): boolean {
  const shape = memberBankKey({ tenant: "", principal: "" });
  return (
    queryKey.length === shape.length && shape.every((part, i) => i < 2 || queryKey[i] === part)
  );
}

/**
 * The R21 key. Absent and empty parameters send the same request, so they share
 * an entry; `role`, `tier`, `job_role`, `skills` and `teams` are sets, so their
 * order does not split one. Every filter the request varies on belongs here, or
 * two different filters would share one cache entry and the table would not
 * refetch when the user changed between them (NG-3222).
 */
export function membersPageKey(scope: QueryScope, query: MembersPageQuery = {}) {
  return scopedKey(scope, "members", "page", {
    q: query.q ?? "",
    order_by: query.order_by ?? "",
    role: [...(query.role ?? [])].sort(),
    tier: [...(query.tier ?? [])].sort(),
    job_role: [...(query.job_role ?? [])].sort(),
    skills: [...(query.skills ?? [])].sort(),
    teams: [...(query.teams ?? [])].sort(),
    per_page: query.per_page ?? null,
  });
}

/**
 * Whether `queryKey` is an R21 key in any scope, for any filter and sort. A
 * member write patches every cached page, and cancels every page read in
 * flight, without naming one. Built from `membersPageKey` itself, so a change to the key reaches the match:
 * the scope and the trailing parameters are free, the parts between are not.
 */
export function isMembersPageKey(queryKey: readonly unknown[]): boolean {
  const shape = membersPageKey({ tenant: "", principal: "" });
  return (
    queryKey.length === shape.length &&
    shape.every((part, i) => i < 2 || i === shape.length - 1 || queryKey[i] === part)
  );
}

/** The R22 key. */
export function memberEligibleRolesKey(scope: QueryScope, memberId: string) {
  return scopedKey(scope, "member", memberId, "eligible-roles");
}

/** R20: the workspace's whole member list, the one `WorkspaceMembersStore.fetchMembers` reads today. */
export function memberBankQuery(service: Pick<WorkspaceService, "listMembers">, scope: QueryScope) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: memberBankKey(scope),
    queryFn: scopedRead((request) => service.listMembers(scope.tenant, request), {
      retry: "safe-read",
    }),
  });
}

/**
 * R21: the members table's pages for one filter and sort. The first page asks
 * with no cursor; each later page asks with
 * the cursor the previous one returned, and there is no next page once the
 * server says so or returns no cursor.
 */
export function membersPageQuery(
  service: Pick<WorkspaceService, "listMembersPage">,
  scope: QueryScope,
  query: MembersPageQuery = {},
) {
  return infiniteQueryOptions({
    ...FRESHNESS.mutable,
    queryKey: membersPageKey(scope, query),
    initialPageParam: null as string | null,
    queryFn: scopedRead<IWorkspaceMembersPage, string | null>(
      (request, { pageParam }) =>
        service.listMembersPage(scope.tenant, { ...query, cursor: pageParam }, request),
      { retry: "safe-read" },
    ),
    getNextPageParam: (last: IWorkspaceMembersPage) =>
      last.next_page_results && last.next_cursor ? last.next_cursor : undefined,
  });
}

/**
 * Every member in R21's pages, once each, in page order: a member can appear on
 * two pages when the list shifts between reads, and the table keys rows by id.
 */
export function rowsOfMembersPages(pages: readonly IWorkspaceMembersPage[]): IWorkspaceMember[] {
  const seen = new Set<string>();
  const rows: IWorkspaceMember[] = [];
  for (const page of pages) {
    for (const member of page.results) {
      if (seen.has(member.id)) continue;
      seen.add(member.id);
      rows.push(member);
    }
  }
  return rows;
}

/** R22: one member's role eligibility, checked when a role picker opens. */
export function memberEligibleRolesQuery(
  service: Pick<WorkspaceService, "fetchMemberEligibleRoles">,
  scope: QueryScope,
  memberId: string,
) {
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: memberEligibleRolesKey(scope, memberId),
    queryFn: scopedRead(
      (request) => service.fetchMemberEligibleRoles(scope.tenant, memberId, request),
      { retry: "safe-read" },
    ),
  });
}

/**
 * R23: one member's profile (name, phone, holiday region), as the member
 * profile form edits it, `[slug, uid, 'member', mid, 'profile']`. Mutable: it
 * is the screen where admins edit those fields.
 */
export function memberProfileKey(scope: QueryScope, memberId: string) {
  return scopedKey(scope, "member", memberId, "profile");
}

/** R23: one member's profile, read when the member profile form opens. */
export function memberProfileQuery(
  service: Pick<WorkspaceService, "getMemberProfile">,
  scope: QueryScope,
  memberId: string,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: memberProfileKey(scope, memberId),
    queryFn: scopedRead((request) => service.getMemberProfile(scope.tenant, memberId, request), {
      retry: "safe-read",
    }),
  });
}

/**
 * R24: one membership's work hours (weekly hours, day pattern, times, timezone,
 * capacity), as the member work hours dialog edits them,
 * `[slug, uid, 'member', mid, 'work-hours']`. Mutable. Keyed by the membership
 * row id, which a role change re-keys, so the dialog reads the live row's key.
 */
export function memberWorkHoursKey(scope: QueryScope, memberId: string) {
  return scopedKey(scope, "member", memberId, "work-hours");
}

/** R24: one membership's work hours, read when the member work hours dialog opens. */
export function memberWorkHoursQuery(
  service: Pick<WorkspaceService, "getMemberWorkHours">,
  scope: QueryScope,
  memberId: string,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: memberWorkHoursKey(scope, memberId),
    queryFn: scopedRead((request) => service.getMemberWorkHours(scope.tenant, memberId, request), {
      retry: "safe-read",
    }),
  });
}

/**
 * The member bank (R20) for a reader outside React: a route loader warming it
 * for the page, or a store that keeps its own lookup. A fresh entry answers
 * with no request, concurrent callers share one, and the answer lands where
 * every `useMemberBank` reader on the page sees it.
 */
export function readMemberBank(
  service: Pick<WorkspaceService, "listMembers">,
  slug: string,
): Promise<IWorkspaceMember[]> {
  const scope = scopeIfReady(slug, "members bank");
  if (!scope) return service.listMembers(slug);
  return getQueryClient().fetchQuery(memberBankQuery(service, scope));
}

/**
 * Warm the members table's first page (R21) for `query` from a route loader,
 * so the table's `useMembersPage` answers from the cache on arrival. Rejects
 * when the read fails, so the loader can log it; the table shows its own
 * error from the same entry.
 */
export async function prefetchMembersPage(
  service: Pick<WorkspaceService, "listMembersPage">,
  slug: string,
  query: MembersPageQuery = {},
): Promise<void> {
  const scope = scopeIfReady(slug, "members page");
  if (!scope) return;
  await getQueryClient().fetchInfiniteQuery(membersPageQuery(service, scope, query));
}

/**
 * A member row this tab has cached, from any members bank (R20) or any
 * members table page (R21), for a lookup made when something opens (a
 * member's Access tab, the work hours dialog). Not an observable read: a
 * caller that must re-render when the bank fills observes it through
 * `useMemberBank` as well.
 */
export function findCachedMember(
  match: (member: IWorkspaceMember) => boolean,
): IWorkspaceMember | undefined {
  const client = peekQueryClient();
  if (!client) return undefined;
  for (const [, members] of client.getQueriesData<IWorkspaceMember[]>({
    predicate: ({ queryKey }) => isMemberBankKey(queryKey),
  })) {
    const member = members?.find(match);
    if (member) return member;
  }
  for (const [, data] of client.getQueriesData<InfiniteData<IWorkspaceMembersPage>>({
    predicate: ({ queryKey }) => isMembersPageKey(queryKey),
  })) {
    for (const page of data?.pages ?? []) {
      const member = page.results.find(match);
      if (member) return member;
    }
  }
  return undefined;
}
